import { useState } from 'react';
import { Palette, Copy, Check } from 'lucide-react';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { DensityToggle } from '@/components/DensityToggle';
import { ThemeButton } from '@/components/ui/theme/ThemeButton';
import { ThemeBadge } from '@/components/ui/theme/ThemeBadge';
import { ThemeInput } from '@/components/ui/theme/ThemeInput';
import { ThemeCard, ThemeCardHeader, ThemeCardTitle, ThemeCardContent } from '@/components/ui/theme/ThemeCard';

interface ColorVariable {
    name: string;
    value: string;
    description: string;
    category: 'core' | 'layout' | 'components';
}

interface PaletteInfo {
    id: string;
    name: string;
    description: string;
    colors: ColorVariable[];
}

export function DesignSystemPage() {
    const [selectedPalette, setSelectedPalette] = useState('slate');
    const [copiedValue, setCopiedValue] = useState<string | null>(null);

    // Define all palettes with their color variables
    const palettes: PaletteInfo[] = [
        {
            id: 'slate',
            name: 'Slate Professional',
            description: 'Cool slate tones for enterprise applications',
            colors: [
                // Core colors
                { name: 'background', value: 'var(--background)', description: 'Main background color', category: 'core' },
                { name: 'foreground', value: 'var(--foreground)', description: 'Main text color', category: 'core' },
                { name: 'primary', value: 'var(--primary)', description: 'Primary brand color', category: 'core' },
                { name: 'secondary', value: 'var(--secondary)', description: 'Secondary brand color', category: 'core' },
                { name: 'accent', value: 'var(--accent)', description: 'Accent color for highlights', category: 'core' },
                { name: 'muted', value: 'var(--muted)', description: 'Muted background color', category: 'core' },
                { name: 'border', value: 'var(--border)', description: 'Border color', category: 'core' },
                { name: 'ring', value: 'var(--ring)', description: 'Focus ring color', category: 'core' },

                // Layout colors
                { name: 'topbar-bg', value: 'var(--topbar-bg)', description: 'Top navigation background', category: 'layout' },
                { name: 'sidebar-bg', value: 'var(--sidebar-bg)', description: 'Sidebar background', category: 'layout' },
                { name: 'sidebar-active', value: 'var(--sidebar-active)', description: 'Active sidebar item', category: 'layout' },
                { name: 'main-bg', value: 'var(--main-bg)', description: 'Main content background', category: 'layout' },
                { name: 'panel-bg', value: 'var(--panel-bg)', description: 'Panel background', category: 'layout' },
                { name: 'card-bg', value: 'var(--card-bg)', description: 'Card background', category: 'layout' },

                // Component colors
                { name: 'button-bg-primary', value: 'var(--button-bg-primary)', description: 'Primary button background', category: 'components' },
                { name: 'button-bg-secondary', value: 'var(--button-bg-secondary)', description: 'Secondary button background', category: 'components' },
                { name: 'button-bg-ghost', value: 'var(--button-bg-ghost)', description: 'Ghost button background', category: 'components' },
                { name: 'badge-bg-neutral', value: 'var(--badge-bg-neutral)', description: 'Neutral badge background', category: 'components' },
                { name: 'badge-bg-info', value: 'var(--badge-bg-info)', description: 'Info badge background', category: 'components' },
                { name: 'badge-bg-success', value: 'var(--badge-bg-success)', description: 'Success badge background', category: 'components' },
                { name: 'badge-bg-warning', value: 'var(--badge-bg-warning)', description: 'Warning badge background', category: 'components' },
                { name: 'badge-bg-danger', value: 'var(--badge-bg-danger)', description: 'Danger badge background', category: 'components' },
            ]
        },
        {
            id: 'emerald',
            name: 'Emerald Gradient',
            description: 'Vibrant green palette with nature-inspired tones',
            colors: [
                // Core colors
                { name: 'background', value: 'var(--background)', description: 'Main background color', category: 'core' },
                { name: 'foreground', value: 'var(--foreground)', description: 'Main text color', category: 'core' },
                { name: 'primary', value: 'var(--primary)', description: 'Primary brand color', category: 'core' },
                { name: 'secondary', value: 'var(--secondary)', description: 'Secondary brand color', category: 'core' },
                { name: 'accent', value: 'var(--accent)', description: 'Accent color for highlights', category: 'core' },
                { name: 'muted', value: 'var(--muted)', description: 'Muted background color', category: 'core' },
                { name: 'border', value: 'var(--border)', description: 'Border color', category: 'core' },
                { name: 'ring', value: 'var(--ring)', description: 'Focus ring color', category: 'core' },

                // Layout colors
                { name: 'topbar-bg', value: 'var(--topbar-bg)', description: 'Top navigation background', category: 'layout' },
                { name: 'sidebar-bg', value: 'var(--sidebar-bg)', description: 'Sidebar background', category: 'layout' },
                { name: 'sidebar-active', value: 'var(--sidebar-active)', description: 'Active sidebar item', category: 'layout' },
                { name: 'main-bg', value: 'var(--main-bg)', description: 'Main content background', category: 'layout' },
                { name: 'panel-bg', value: 'var(--panel-bg)', description: 'Panel background', category: 'layout' },
                { name: 'card-bg', value: 'var(--card-bg)', description: 'Card background', category: 'layout' },

                // Component colors
                { name: 'button-bg-primary', value: 'var(--button-bg-primary)', description: 'Primary button background', category: 'components' },
                { name: 'button-bg-secondary', value: 'var(--button-bg-secondary)', description: 'Secondary button background', category: 'components' },
                { name: 'button-bg-ghost', value: 'var(--button-bg-ghost)', description: 'Ghost button background', category: 'components' },
                { name: 'badge-bg-neutral', value: 'var(--badge-bg-neutral)', description: 'Neutral badge background', category: 'components' },
                { name: 'badge-bg-info', value: 'var(--badge-bg-info)', description: 'Info badge background', category: 'components' },
                { name: 'badge-bg-success', value: 'var(--badge-bg-success)', description: 'Success badge background', category: 'components' },
                { name: 'badge-bg-warning', value: 'var(--badge-bg-warning)', description: 'Warning badge background', category: 'components' },
                { name: 'badge-bg-danger', value: 'var(--badge-bg-danger)', description: 'Danger badge background', category: 'components' },
            ]
        }
    ];

    const currentPalette = palettes.find(p => p.id === selectedPalette) || palettes[0];

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedValue(value);
            setTimeout(() => setCopiedValue(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const getColorPreview = (colorVar: string) => {
        // Extract the actual color value from CSS variable
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue(colorVar.replace('var(', '').replace(')', '')).trim();
    };

    const groupedColors = currentPalette.colors.reduce((acc, color) => {
        if (!acc[color.category]) {
            acc[color.category] = [];
        }
        acc[color.category].push(color);
        return acc;
    }, {} as Record<string, ColorVariable[]>);

    return (
        <div className="min-h-screen bg-[hsl(var(--main-bg))] text-[hsl(var(--main-fg))] p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-[hsl(var(--main-fg))]">Design System</h1>
                        <p className="text-[hsl(var(--muted-foreground))] mt-2">
                            Explore color palettes and design tokens
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <DensityToggle />
                        <ThemeSwitcher />
                    </div>
                </div>

                {/* Palette Selector */}
                <div className="flex items-center gap-4 mb-6">
                    <label className="text-sm font-medium text-[hsl(var(--main-fg))]">Select Palette:</label>
                    <select
                        value={selectedPalette}
                        onChange={(e) => setSelectedPalette(e.target.value)}
                        className="px-3 py-2 border border-[hsl(var(--border))] rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                    >
                        {palettes.map((palette) => (
                            <option key={palette.id} value={palette.id}>
                                {palette.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Current Palette Info */}
                <ThemeCard className="mb-8">
                    <ThemeCardHeader>
                        <ThemeCardTitle className="flex items-center gap-2">
                            <Palette className="h-5 w-5" />
                            {currentPalette.name}
                        </ThemeCardTitle>
                        <p className="text-[hsl(var(--muted-foreground))]">{currentPalette.description}</p>
                    </ThemeCardHeader>
                </ThemeCard>
            </div>

            {/* Color Variables Grid */}
            <div className="space-y-8">
                {Object.entries(groupedColors).map(([category, colors]) => (
                    <div key={category}>
                        <h2 className="text-xl font-semibold text-[hsl(var(--main-fg))] mb-4 capitalize">
                            {category} Colors
                        </h2>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {colors.map((color) => (
                                <ThemeCard key={color.name} className="relative">
                                    <ThemeCardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            {/* Color Preview */}
                                            <div
                                                className="w-12 h-12 rounded-lg border border-[hsl(var(--border))] flex-shrink-0"
                                                style={{ background: color.value }}
                                                title={getColorPreview(color.value)}
                                            />

                                            {/* Color Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <code className="text-sm font-mono text-[hsl(var(--primary))] bg-[hsl(var(--muted))] px-2 py-1 rounded">
                                                        {color.name}
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(color.value)}
                                                        className="p-1 hover:bg-[hsl(var(--muted))] rounded transition-colors"
                                                        title="Copy CSS variable"
                                                    >
                                                        {copiedValue === color.value ? (
                                                            <Check className="h-3 w-3 text-green-600" />
                                                        ) : (
                                                            <Copy className="h-3 w-3" />
                                                        )}
                                                    </button>
                                                </div>
                                                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
                                                    {color.description}
                                                </p>
                                                <code className="text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-1 rounded block">
                                                    {color.value}
                                                </code>
                                            </div>
                                        </div>
                                    </ThemeCardContent>
                                </ThemeCard>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Component Examples */}
            <div className="mt-12">
                <h2 className="text-xl font-semibold text-[hsl(var(--main-fg))] mb-6">
                    Component Examples
                </h2>

                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {/* Buttons */}
                    <ThemeCard>
                        <ThemeCardHeader>
                            <ThemeCardTitle>Buttons</ThemeCardTitle>
                        </ThemeCardHeader>
                        <ThemeCardContent className="space-y-3">
                            <ThemeButton variant="primary">Primary</ThemeButton>
                            <ThemeButton variant="secondary">Secondary</ThemeButton>
                            <ThemeButton variant="ghost">Ghost</ThemeButton>
                            <ThemeButton variant="destructive">Destructive</ThemeButton>
                        </ThemeCardContent>
                    </ThemeCard>

                    {/* Badges */}
                    <ThemeCard>
                        <ThemeCardHeader>
                            <ThemeCardTitle>Badges</ThemeCardTitle>
                        </ThemeCardHeader>
                        <ThemeCardContent className="space-y-3">
                            <div className="flex flex-wrap gap-2">
                                <ThemeBadge variant="neutral">Neutral</ThemeBadge>
                                <ThemeBadge variant="info">Info</ThemeBadge>
                                <ThemeBadge variant="success">Success</ThemeBadge>
                                <ThemeBadge variant="warning">Warning</ThemeBadge>
                                <ThemeBadge variant="danger">Danger</ThemeBadge>
                            </div>
                        </ThemeCardContent>
                    </ThemeCard>

                    {/* Inputs */}
                    <ThemeCard>
                        <ThemeCardHeader>
                            <ThemeCardTitle>Inputs</ThemeCardTitle>
                        </ThemeCardHeader>
                        <ThemeCardContent className="space-y-3">
                            <ThemeInput placeholder="Default input" />
                            <ThemeInput placeholder="Filled input" variant="filled" />
                            <ThemeInput placeholder="Outlined input" variant="outlined" />
                        </ThemeCardContent>
                    </ThemeCard>
                </div>
            </div>
        </div>
    );
}





























